import type { RenderableElement } from "../components/canvasBuilder/CanvasRenderer";
import { resolveElementType } from "../components/canvasBuilder/widgetElementType";
import { isWithinContainer, type ScopeNode } from "./canvasSelectionScope";
import {
  isReadOnlyFieldConfig,
  isWritableAccess,
  visibleRuntimeElements,
} from "./runtimeElementAccess";

export interface CanvasConversionOptions {
  hasRow?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  selectionMode?: string;
  selectionContainerId?: string | null;
}

/** Convert the projected API payload using the runtime's layout and access mapping. */
export function toRenderableElements(
  // API responses still include legacy element shapes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canvas: { elements?: any[]; embeddings?: any[] },
  options: CanvasConversionOptions = {},
): RenderableElement[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawElements = visibleRuntimeElements((canvas.elements || []) as any[]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawEmbeddings = (canvas.embeddings || []) as any[];
  const embeddingByElementId = new Map<string, any>();
  for (const emb of rawEmbeddings) {
    const elementId = emb?.element_id || emb?.elementId;
    if (elementId) embeddingByElementId.set(String(elementId), emb);
  }

  return rawElements.map((el, idx) => {
    // Transform data_binding from backend format
    const dataBinding = el.data_binding
      ? {
          data_source_id:
            el.data_binding.data_source_alias || el.data_binding.data_source_id,
          field_path: el.data_binding.field_path,
        }
      : undefined;

    // Get element type (lowercase)
    const rawType = (el.element_type || el.type || "").toLowerCase();
    const type = resolveElementType(rawType, el.config_json || el.props);

    // Get variant from config_json or props
    // Map fieldType (agent convention) to variant (renderer convention)
    const baseProps = el.config_json || el.props || {};
    const embedding = el.id
      ? embeddingByElementId.get(String(el.id))
      : undefined;
    const props =
      type === "embedded_canvas" && embedding
        ? {
            childCanvasId: embedding.child_canvas_id || embedding.childCanvasId,
            parentDataSourceAlias:
              embedding.parent_data_source_alias ||
              embedding.parentDataSourceAlias,
            parentKeyField:
              embedding.parent_key_field || embedding.parentKeyField,
            childDataSourceAlias:
              embedding.child_data_source_alias ||
              embedding.childDataSourceAlias,
            childFilterField:
              embedding.child_filter_field || embedding.childFilterField,
            ...(baseProps || {}),
          }
        : baseProps || {};
    const runtimeProps =
      type === "field" &&
      (!isWritableAccess(el.effective_access) ||
        isReadOnlyFieldConfig(props) ||
        (options.selectionMode &&
          options.selectionMode !== "edit" &&
          options.selectionContainerId &&
          el.id &&
          isWithinContainer(
            String(el.id),
            options.selectionContainerId,
            (canvas.elements || []) as ScopeNode[],
          )) ||
        (options.hasRow
          ? options.canEdit !== true
          : options.canCreate !== true))
        ? { ...props, readonly: true }
        : props;
    const variant = runtimeProps.fieldType || (runtimeProps.variant as string);

    return {
      id: el.id || `el-${idx}`,
      type,
      effectiveAccess: el.effective_access,
      variant,
      props: runtimeProps,
      // Use grid positioning from backend
      gridColumn: el.grid_column || 1,
      gridRow: el.grid_row || idx + 1,
      columnSpan: el.column_span || 12,
      rowSpan: el.row_span || 1,
      data_binding: dataBinding,
      // Nesting support
      parentElementId: el.parent_element_id || undefined,
      parentPanelId: el.parent_panel_id || undefined,
      displayOrder: el.sort_order ?? el.display_order ?? idx,
      styleOverrides: el.style_overrides ?? undefined,
      isVisible: el.is_visible ?? el.isVisible,
      visibilityRules: el.visibility_rules ?? el.visibilityRules ?? undefined,
    };
  });
}
